import { FullWidthBanner } from '@systemeio/components';
import { LogOut01Icon, PlusCircleIcon } from '@systemeio/icons';
import { BadRequest } from '@systemeio/util';
import { createRuleEmail, createRuleSpecificEmail } from 'modules/automation-rules/email/api/rule-email-api';
import { FilterEmailApiInterface } from 'modules/automation-rules/types/automation-rule-filter-interface';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Breadcrumbs, Button, FormInput, Modal, toast, Tooltip, type BreadcrumbsData } from 'shared/components';
import DashboardMailEditor from 'shared/components/dashboard-mail-editor';
import { MailingPoolStatusEnum, MailingPoolStatusHelpMessages } from 'shared/components/dashboard-mail-editor/enums/mailing-pool-status-enum';
import { UnsavedEmailsEnum } from 'shared/components/dashboard-mail-editor/enums/unsaved-emails-enum';
import { useEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-email-options';
import { useUnsavedEmail } from 'shared/components/dashboard-mail-editor/hooks/use-unsaved-email';
import { AttachmentFileInterface, EmailBaseInterface, EmailErrorResponseContentInterface, EmailErrorType, EmailRecipientErrorResponseContentInterface, EmailRecipientInterface, UnsavedAttachmentFileInterface } from 'shared/components/dashboard-mail-editor/types/email-interface';
import { getEmailStatusLink } from 'shared/components/dashboard-mail-editor/utils/get-email-status-link';
import { getMailingUpdateData } from 'shared/components/dashboard-mail-editor/utils/get-mailing-update-data';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { Page } from 'shared/components/page';
import SenderEmailSelect from 'shared/components/sender-email-select';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useMediaQuery from 'shared/hooks/use-media-query';
import { CONTENT_CLASSNAME } from 'shared/layout/constants/layout-class-names';
import { isValidEmail } from 'shared/utils/email-validation';
import { KeyedMutator } from 'swr';
import { twMerge } from 'tailwind-merge';
const defaultValues: EmailRecipientInterface = {
  body: '',
  subject: '',
  previewText: '',
  fromName: '',
  fromEmail: '',
  recipient: ''
};
const defaultErrors: EmailErrorType = {
  body: '',
  fromEmail: '',
  fromName: '',
  subject: '',
  previewText: '',
  common: []
};
type CreateEmailFlowStep = 'closed' | 'setup' | 'editor';
interface RuleEmailCreateFlowProps {
  withRecipient?: boolean;
  mutateEmailData: KeyedMutator<FilterEmailApiInterface[]> | undefined;
  mutateSpecificEmailData: KeyedMutator<FilterEmailApiInterface[]> | undefined;
  onSetNewEmail: (data: {
    id: number;
    subject: string;
  }) => void;
}
const RuleEmailCreateFlow = ({
  withRecipient,
  mutateEmailData,
  mutateSpecificEmailData,
  onSetNewEmail
}: RuleEmailCreateFlowProps) => {
  const {
    t
  } = useLocoTranslation();
  const {
    isMobile
  } = useMediaQuery();
  const router = useRouter();
  const [step, setStep] = useState<CreateEmailFlowStep>('closed');
  const [isFetching, setIsFetching] = useState(false);
  const [tempState, setTempState] = useState<EmailRecipientInterface>(defaultValues);
  const [errors, setErrors] = useState<EmailErrorType>(defaultErrors);
  const [attachmentsTempState, setAttachmentsTempState] = useState<(AttachmentFileInterface | UnsavedAttachmentFileInterface)[]>([]);
  const {
    data: emailOptions
  } = useEmailOptions(step !== 'closed');
  useEffect(() => {
    if (!emailOptions || step !== 'setup') return;
    setTempState(prev => ({
      ...prev,
      fromEmail: prev.fromEmail || emailOptions.senderEmailAddress,
      fromName: prev.fromName || emailOptions.senderName,
      recipient: prev.recipient || (withRecipient ? emailOptions.testEmailAddress : '')
    }));
  }, [emailOptions, step, withRecipient]);
  const closeFlow = () => setStep('closed');
  const onCreate = async () => {
    try {
      if (tempState) {
        setErrors(defaultErrors);
        if (!isValidEmail(tempState.fromEmail)) {
          return setErrors(prev_4 => ({
            ...prev_4,
            fromEmail: t('dashboard.validation.email_not_valid')
          }));
        }
        if (withRecipient && !isValidEmail(tempState.recipient)) {
          return setErrors(prev_5 => ({
            ...prev_5,
            recipient: t('dashboard.validation.email_not_valid')
          }));
        }
        setIsFetching(true);
        const currentEmail_0 = getMailingUpdateData({
          fromEmail: tempState.fromEmail,
          fromName: tempState.fromName,
          subject: tempState.subject,
          previewText: tempState.previewText,
          body: tempState.body
        }, attachmentsTempState);
        if (withRecipient) {
          const emailResponse = await createRuleSpecificEmail({
            recipientAddress: tempState.recipient,
            mailing: {
              currentEmail: currentEmail_0
            }
          });
          if (mutateSpecificEmailData) {
            await mutateSpecificEmailData(data => {
              if (data) {
                return [{
                  id: emailResponse.data.id,
                  subject: emailResponse.data.mailing.currentEmail.subject
                }, ...data];
              }
            }, false);
          }
          onSetNewEmail({
            id: emailResponse.data.id,
            subject: emailResponse.data.mailing.currentEmail.subject
          });
        } else {
          const emailResponse_0 = await createRuleEmail(currentEmail_0);
          if (mutateEmailData) {
            await mutateEmailData(data_0 => {
              if (data_0) {
                return [{
                  id: emailResponse_0.data.currentEmail.id,
                  subject: emailResponse_0.data.currentEmail.subject
                }, ...data_0];
              }
            }, false);
          }
          onSetNewEmail({
            id: emailResponse_0.data.currentEmail.id,
            subject: emailResponse_0.data.currentEmail.subject
          });
        }
        toast.success(t('global.changes_saved'));
        await removeDbRecord();
        setIsFetching(false);
        closeFlow();
      }
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        if (withRecipient) {
          const errors_0 = e.errors as EmailRecipientErrorResponseContentInterface;
          if (errors_0.fields) {
            if (errors_0.fields.recipientAddress) {
              setErrors(prev_0 => ({
                ...prev_0,
                recipient: errors_0.fields.recipientAddress?.join('')
              }));
            }
            const currentEmail = errors_0.fields.mailing?.currentEmail;
            if (currentEmail) {
              setErrors(prev_1 => ({
                ...prev_1,
                body: currentEmail.body?.join(''),
                fromEmail: currentEmail.fromEmail?.join(''),
                fromName: currentEmail.fromName?.join(''),
                subject: currentEmail.subject?.join(''),
                previewText: currentEmail.previewText?.join('')
              }));
            }
          }
        } else {
          const errors_1 = e.errors as EmailErrorResponseContentInterface;
          if (errors_1.fields) {
            const fields = errors_1.fields as unknown as Record<keyof EmailBaseInterface, string[]>;
            setErrors(prev_2 => ({
              ...prev_2,
              body: fields.body?.join(''),
              fromEmail: fields.fromEmail?.join(''),
              fromName: fields.fromName?.join(''),
              subject: fields.subject?.join(''),
              previewText: fields.previewText?.join('')
            }));
          }
        }
        if (e.errors.common) {
          const commonErrors = e.errors.common;
          setErrors(prev_3 => {
            return {
              ...prev_3,
              common: commonErrors
            };
          });
        }
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  const {
    UnsavedEmailConfirmationModal,
    removeDbRecord,
    setNeedToShowModal
  } = useUnsavedEmail({
    type: withRecipient ? UnsavedEmailsEnum.create_rule_email_with_recipient : UnsavedEmailsEnum.create_rule_email,
    currentBody: tempState?.body,
    initialBody: '',
    onPaste: (data_1: string) => {
      setTempState(prev_6 => ({
        ...prev_6,
        body: data_1
      }));
    }
  });
  const onChangeEmail = (value: EmailRecipientInterface) => setTempState(value);
  const isSetupValid = tempState.subject.trim().length > 0 && tempState.fromName.trim().length > 0 && isValidEmail(tempState.fromEmail);
  const deliverabilityMessageKey = emailOptions && (emailOptions.emailPool === MailingPoolStatusEnum.high_risk || emailOptions.emailPool === MailingPoolStatusEnum.quarantine) ? MailingPoolStatusHelpMessages[emailOptions.emailPool] : undefined;
  const breadcrumbsData: BreadcrumbsData[] = [{
    caption: t('dashboard.automation_rule.title_plural'),
    href: '/automation-rules',
    isStatic: true
  }, {
    caption: t(router.pathname.includes('/create') ? 'dashboard.automation_rule.edit.create_title' : 'dashboard.automation_rule.edit.title'),
    isStatic: true
  }, {
    caption: tempState.subject,
    isStatic: true
  }];
  return <>
      <Tooltip mode={'hover'} label={t('dashboard.automation_rule.edit.create_email')}>
        <Button aria-label={t('dashboard.automation_rule.edit.create_email')} onClick={() => setStep('setup')} size="icon-sm" variant="gray">
          <PlusCircleIcon />
        </Button>
      </Tooltip>
      <Modal afterEnter={() => setNeedToShowModal(true)} isFetching={isFetching} opened={step === 'setup'} onClose={closeFlow} title={t('dashboard.automation_rule.edit.create_email')} footer={<>
            <Button type="button" onClick={closeFlow} variant="secondary" size="md">
              {t('global.cancel')}
            </Button>
            <Button type="button" disabled={!isSetupValid} onClick={() => setStep('editor')} variant="primary" size="md">
              {t('dashboard.automation_rule.edit.save_and_edit')}
            </Button>
          </>}>
        <div className="tablet:-my-2 max-tablet:min-h-135 max-tablet:py-4 flex flex-col gap-4">
          <FormInput isPreFetching={!emailOptions} label={t('dashboard.email.subject')} required value={tempState.subject} onChange={event => setTempState(prev_7 => ({
          ...prev_7,
          subject: event.target.value
        }))} />
          <div className="flex flex-col gap-1">
            <FormInput isPreFetching={!emailOptions} label={t('dashboard.email.sender_name')} required value={tempState.fromName} onChange={event_0 => setTempState(prev_8 => ({
            ...prev_8,
            fromName: event_0.target.value
          }))} />
            <span className="text-text-secondary text-sm">
              <TransLoco t={t} i18nKey="dashboard.automation_rule.edit.sender_name_description" components={{
              a: <LinkWithoutPrefetch key="mailing-settings-link" className="link" href="/profile/mailing-settings" target="_blank" />
            }} />
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <SenderEmailSelect isPreFetching={!emailOptions} noHint={!isMobile} required value={tempState.fromEmail} onChange={fromEmail => setTempState(prev_9 => ({
            ...prev_9,
            fromEmail: fromEmail || ''
          }))} />
            <LinkWithoutPrefetch className="link text-sm" href="/profile/mailing-settings#verified-email-addresses" target="_blank">
              {t('dashboard.automation_rule.edit.add_and_verify_email')}
            </LinkWithoutPrefetch>
          </div>
        </div>
      </Modal>
      {step === 'editor' && <div className="bg-bg-page fixed inset-x-0 top-(--dashboard-navbar-height) bottom-0 z-50 overflow-y-auto overscroll-contain [--sticky-top:0px]">
          <Page className={twMerge(CONTENT_CLASSNAME, 'max-tablet:pb-24 tablet:py-3 desktop:py-4 py-4')}>
            <Page.Header className="px-0" actions={[<Button className="w-22.5" type="button" disabled={isFetching} onClick={closeFlow} variant="secondary" size="sm">
                  <LogOut01Icon />
                  {t('global.exit')}
                </Button>, <Button className="w-22.5" type="button" isLoading={isFetching} onClick={onCreate} variant="primary" size="sm">
                  {t('global.save')}
                </Button>]}>
              <Breadcrumbs data={breadcrumbsData} />
            </Page.Header>
            {deliverabilityMessageKey && <Page.Banner>
                <FullWidthBanner variant="warning">
                  <TransLoco t={t} i18nKey={deliverabilityMessageKey} components={{
              a: <a className="link" href={getEmailStatusLink()} target="_blank" rel="noreferrer" />
            }} />
                </FullWidthBanner>
              </Page.Banner>}
            <Page.Body>
              <DashboardMailEditor withRecipient={withRecipient} isPreFetching={!emailOptions} tempState={tempState} onChangeEmail={onChangeEmail} attachmentsTempState={attachmentsTempState} setAttachmentsTempState={setAttachmentsTempState} errors={errors} />
            </Page.Body>
          </Page>
        </div>}
      {UnsavedEmailConfirmationModal}
    </>;
};
export default RuleEmailCreateFlow;